/**
 * ArticleImageUpload
 * Gets Article Images into the editor: by the toolbar file picker
 * (insertArticleImageFiles), clipboard paste and drag & drop.
 *
 * Each image is uploaded before it becomes an ArticleImage node, so the body
 * only ever references server-issued ids. While a file uploads the editor shows
 * a spinner placeholder. The placeholder is a widget decoration, not a node, so
 * it can never leak into getHTML() - saving mid-upload simply omits the image.
 * On failure the placeholder is removed and the reason is reported through
 * `onError` (the editor turns it into a toast).
 *
 * Pasted or dropped HTML containing images (from another article, a web page,
 * or this editor) is never trusted to reference an image by id or URL: every
 * image is fetched and uploaded as a new Article Image at its original spot in
 * the pasted content, so the server's "image belongs to another article"
 * rejection is only a safety net. An image that cannot be fetched is dropped
 * with a message.
 */
import { Extension, type Editor } from '@tiptap/core';
import { Plugin, PluginKey, type EditorState, type Transaction } from '@tiptap/pm/state';
import { Decoration, DecorationSet, type EditorView } from '@tiptap/pm/view';
import { DOMParser as ProseMirrorDOMParser, Slice } from '@tiptap/pm/model';
import { dropPoint } from '@tiptap/pm/transform';
import type { HelpImageUpload, ImagePlacement, ImageSize } from '@app-types';
import {
  DEFAULT_IMAGE_PLACEMENT,
  IMAGE_UPLOAD_MESSAGES,
  getDefaultImageSize,
  getImageFileError,
  getImageUploadErrorMessage,
  isImagePlacement,
  isImageSize
} from '../articleImageHelpers';
import { ARTICLE_IMAGE_NODE } from './ArticleImage';

export const UPLOAD_PLACEHOLDER_ATTRIBUTE = 'data-article-image-uploading';
export const UPLOAD_PLACEHOLDER_CLASS = 'article-image-uploading';

export interface ArticleImageUploadOptions {
  /** Uploads one image file and resolves with its id and preview URL. */
  upload: (file: File) => Promise<HelpImageUpload>;
  /** Receives a user-facing reason whenever an image is not inserted. */
  onError: (message: string) => void;
  /** Downloads a pasted image so it can be re-uploaded. */
  fetchImage: (src: string) => Promise<Blob>;
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    articleImageUpload: {
      /** Uploads the files and inserts them at the current selection. */
      insertArticleImageFiles: (files: File[]) => ReturnType;
    };
  }
}

/** Caption/Placement/Size carried over from pasted figure markup. */
interface CarriedImageAttributes {
  caption: string;
  placement?: ImagePlacement;
  size?: ImageSize;
}

interface PendingUpload {
  id: string;
  getFile: () => Promise<File>;
  attributes: CarriedImageAttributes;
}

interface PlaceholderSpec {
  id: string;
}

type PlaceholderAction = { add: { id: string; pos: number }[] } | { remove: string };

export const uploadPlaceholderKey = new PluginKey<DecorationSet>('articleImageUploadPlaceholder');

let uploadSequence = 0;
const nextUploadId = (): string => {
  uploadSequence += 1;
  return `article-image-upload-${uploadSequence}`;
};

/**
 * Private-use characters delimit the slot markers left in pasted HTML where
 * each image was, so a marker cannot collide with real article text.
 */
const SLOT_START = '';
const SLOT_END = '';
const slotMarker = (id: string): string => `${SLOT_START}${id}${SLOT_END}`;

const defaultFetchImage = async (src: string): Promise<Blob> => {
  const response = await fetch(src);
  if (!response.ok) throw new Error(`Image download failed with status ${response.status}`);
  return response.blob();
};

const createPlaceholderElement = (): HTMLElement => {
  const element = document.createElement('div');
  element.className = UPLOAD_PLACEHOLDER_CLASS;
  element.setAttribute(UPLOAD_PLACEHOLDER_ATTRIBUTE, 'true');
  element.setAttribute('data-test-id', 'article-image-uploading');
  element.setAttribute('role', 'status');
  element.setAttribute('contenteditable', 'false');

  const spinner = document.createElement('span');
  spinner.className = `${UPLOAD_PLACEHOLDER_CLASS}-spinner`;
  spinner.setAttribute('aria-hidden', 'true');

  const label = document.createElement('span');
  label.textContent = 'Uploading image…';

  element.append(spinner, label);
  return element;
};

/** Position of a still-present placeholder, or null once its spot was deleted. */
export const findUploadPlaceholder = (state: EditorState, id: string): number | null => {
  const decorations = uploadPlaceholderKey.getState(state);
  const found = decorations?.find(undefined, undefined, (spec: PlaceholderSpec) => spec.id === id) ?? [];
  return found.length > 0 ? found[0].from : null;
};

const addPlaceholders = (tr: Transaction, placeholders: { id: string; pos: number }[]): Transaction => {
  const action: PlaceholderAction = { add: placeholders };
  return tr.setMeta(uploadPlaceholderKey, action);
};

const removePlaceholder = (view: EditorView, id: string): void => {
  const action: PlaceholderAction = { remove: id };
  view.dispatch(view.state.tr.setMeta(uploadPlaceholderKey, action));
};

const toFile = (blob: Blob): File =>
  blob instanceof File ? blob : new File([blob], 'pasted-image', { type: blob.type });

/** Image files carried by a paste or drop. Non-image files are reported, not ignored. */
const getTransferFiles = (data: DataTransfer | null): File[] => Array.from(data?.files ?? []);

const readCarriedAttributes = (img: HTMLImageElement, figure: HTMLElement | null): CarriedImageAttributes => {
  const caption = (figure?.querySelector('figcaption')?.textContent ?? img.getAttribute('alt') ?? '').trim();
  const placement = figure?.getAttribute('data-placement');
  const size = figure?.getAttribute('data-size');
  return {
    caption,
    placement: isImagePlacement(placement) ? placement : undefined,
    size: isImageSize(size) ? size : undefined
  };
};

interface PastedImageSlot {
  id: string;
  src: string | null;
  attributes: CarriedImageAttributes;
}

/**
 * Replaces every image in the pasted HTML with a slot marker - a whole
 * paragraph for a figure (block-level), inline text for a bare img - and
 * returns the images in document order.
 */
const extractImageSlots = (body: HTMLElement): PastedImageSlot[] => {
  const slots: PastedImageSlot[] = [];

  Array.from(body.querySelectorAll('img')).forEach((img) => {
    // A figure holding two images was already replaced along with the first.
    if (!img.isConnected) return;

    const figure = img.closest('figure');
    const id = nextUploadId();
    const marker = body.ownerDocument.createElement(figure ? 'p' : 'span');
    marker.textContent = slotMarker(id);
    (figure ?? img).replaceWith(marker);

    slots.push({ id, src: img.getAttribute('src'), attributes: readCarriedAttributes(img, figure) });
  });

  return slots;
};

/**
 * Removes each slot marker from the document (with its paragraph when the
 * marker stood alone) and returns where each image's placeholder belongs.
 */
const replaceSlotMarkers = (tr: Transaction, slots: PastedImageSlot[]): { id: string; pos: number }[] => {
  const ranges: { id: string; from: number; to: number }[] = [];

  tr.doc.descendants((node, pos) => {
    if (!node.isText || !node.text) return;
    slots.forEach((slot) => {
      const marker = slotMarker(slot.id);
      const index = node.text?.indexOf(marker) ?? -1;
      if (index < 0) return;

      const from = pos + index;
      const $from = tr.doc.resolve(from);
      const standsAlone = $from.parent.isTextblock && $from.parent.textContent === marker;
      ranges.push(
        standsAlone
          ? { id: slot.id, from: $from.before(), to: $from.after() }
          : { id: slot.id, from, to: from + marker.length }
      );
    });
  });

  // Delete back to front so earlier ranges stay valid, then map each recorded
  // position through the deletions that happened before it in the document.
  const sorted = [...ranges].sort((a, b) => b.from - a.from);
  const recorded = sorted.map((range) => {
    tr.delete(range.from, range.to);
    return { id: range.id, pos: range.from, step: tr.steps.length };
  });
  return recorded.map(({ id, pos, step }) => ({ id, pos: tr.mapping.slice(step).map(pos, -1) }));
};

/** Drop target position, or null when it cannot be measured (e.g. off-document). */
const getDropPosition = (view: EditorView, event: DragEvent): number | null => {
  try {
    return view.posAtCoords({ left: event.clientX, top: event.clientY })?.pos ?? null;
  } catch {
    return null;
  }
};

/** Pre-checks the files, reporting and skipping the obviously invalid ones. */
const prepareFileUploads = (files: File[], options: ArticleImageUploadOptions): PendingUpload[] =>
  files.flatMap((file) => {
    const error = getImageFileError(file);
    if (error) {
      options.onError(error);
      return [];
    }
    return [{ id: nextUploadId(), getFile: () => Promise.resolve(file), attributes: { caption: '' } }];
  });

/**
 * Uploads one image and swaps its placeholder for an ArticleImage node. Every
 * state access happens after an await, so the caller's placeholder
 * transaction is always applied first.
 */
const runUpload = async (
  editor: Editor,
  options: ArticleImageUploadOptions,
  { id, getFile, attributes }: PendingUpload
): Promise<void> => {
  const fail = (message: string) => {
    if (!editor.isDestroyed) removePlaceholder(editor.view, id);
    options.onError(message);
  };

  let file: File;
  try {
    file = await getFile();
  } catch {
    fail(IMAGE_UPLOAD_MESSAGES.copyFailed);
    return;
  }

  const fileError = getImageFileError(file);
  if (fileError) {
    fail(fileError);
    return;
  }

  let uploaded: HelpImageUpload;
  try {
    uploaded = await options.upload(file);
  } catch (error) {
    fail(getImageUploadErrorMessage(error));
    return;
  }

  // The editor closed, or the Admin deleted the spot, while uploading.
  if (editor.isDestroyed) return;
  const pos = findUploadPlaceholder(editor.state, id);
  if (pos === null) return;

  const placement = attributes.placement ?? DEFAULT_IMAGE_PLACEMENT;
  const node = editor.schema.nodes[ARTICLE_IMAGE_NODE].create({
    imageId: uploaded.id,
    src: uploaded.url,
    caption: attributes.caption,
    placement,
    size: attributes.size ?? getDefaultImageSize(placement)
  });
  const removal: PlaceholderAction = { remove: id };
  const tr = editor.state.tr.setMeta(uploadPlaceholderKey, removal);
  tr.replaceRangeWith(pos, pos, node);
  editor.view.dispatch(tr);
};

export const ArticleImageUpload = Extension.create<ArticleImageUploadOptions>({
  name: 'articleImageUpload',

  addOptions() {
    return {
      upload: () => Promise.reject(new Error('ArticleImageUpload: no upload handler configured')),
      onError: () => undefined,
      fetchImage: defaultFetchImage
    };
  },

  addCommands() {
    return {
      insertArticleImageFiles:
        (files: File[]) =>
        ({ tr, dispatch }) => {
          const uploads = prepareFileUploads(files, this.options);
          if (uploads.length === 0) return false;
          if (dispatch) {
            const pos = tr.selection.from;
            addPlaceholders(
              tr,
              uploads.map((upload) => ({ id: upload.id, pos }))
            );
            uploads.forEach((upload) => runUpload(this.editor, this.options, upload));
          }
          return true;
        }
    };
  },

  addProseMirrorPlugins() {
    const { editor, options } = this;

    const startFileUploads = (view: EditorView, files: File[], pos: number): void => {
      const uploads = prepareFileUploads(files, options);
      if (uploads.length === 0) return;
      view.dispatch(
        addPlaceholders(
          view.state.tr,
          uploads.map((upload) => ({ id: upload.id, pos }))
        )
      );
      uploads.forEach((upload) => runUpload(editor, options, upload));
    };

    /**
     * Inserts pasted/dropped HTML with each image swapped for a placeholder,
     * then re-uploads every image into its own spot.
     */
    const insertHtmlWithImages = (view: EditorView, html: string, dropPos: number | null): void => {
      const body = new window.DOMParser().parseFromString(html, 'text/html').body;
      const slots = extractImageSlots(body);

      const parsed = ProseMirrorDOMParser.fromSchema(view.state.schema).parseSlice(body);
      const slice = Slice.maxOpen(parsed.content);

      const tr = view.state.tr;
      if (dropPos === null) {
        tr.replaceSelection(slice);
      } else {
        const insertAt = dropPoint(view.state.doc, dropPos, slice) ?? dropPos;
        tr.replaceRange(insertAt, insertAt, slice);
      }

      const placeholders = replaceSlotMarkers(tr, slots);
      view.dispatch(addPlaceholders(tr, placeholders).setMeta('uiEvent', dropPos === null ? 'paste' : 'drop'));

      const placed = new Set(placeholders.map((placeholder) => placeholder.id));
      slots
        .filter((slot) => placed.has(slot.id))
        .forEach((slot) =>
          runUpload(editor, options, {
            id: slot.id,
            attributes: slot.attributes,
            getFile: async () => {
              if (!slot.src) throw new Error('Pasted image has no source');
              return toFile(await options.fetchImage(slot.src));
            }
          })
        );
    };

    const hasImageMarkup = (html: string): boolean => /<img[\s>/]/i.test(html);

    return [
      new Plugin<DecorationSet>({
        key: uploadPlaceholderKey,
        state: {
          init: () => DecorationSet.empty,
          apply: (tr, decorations) => {
            let next = decorations.map(tr.mapping, tr.doc);
            const action = tr.getMeta(uploadPlaceholderKey) as PlaceholderAction | undefined;
            if (action && 'add' in action) {
              next = next.add(
                tr.doc,
                action.add.map(({ id, pos }) => {
                  const spec: PlaceholderSpec = { id };
                  // `key` lets ProseMirror reuse the widget DOM instead of redrawing the spinner.
                  return Decoration.widget(pos, createPlaceholderElement, { ...spec, key: id });
                })
              );
            } else if (action && 'remove' in action) {
              next = next.remove(next.find(undefined, undefined, (spec: PlaceholderSpec) => spec.id === action.remove));
            }
            return next;
          }
        },
        props: {
          decorations(state) {
            return uploadPlaceholderKey.getState(state);
          },

          handlePaste(view, event) {
            if (!editor.isEditable) return false;

            // A copied screenshot arrives as a file (often alongside an <img>
            // HTML fallback); the file is the real image, so it wins.
            const files = getTransferFiles(event.clipboardData);
            if (files.length > 0) {
              startFileUploads(view, files, view.state.selection.from);
              return true;
            }

            const html = event.clipboardData?.getData('text/html') ?? '';
            if (!hasImageMarkup(html)) return false;
            insertHtmlWithImages(view, html, null);
            return true;
          },

          handleDrop(view, event, _slice, moved) {
            // Moving an existing image within this editor keeps its id.
            if (moved || !editor.isEditable) return false;

            const files = getTransferFiles(event.dataTransfer);
            const html = event.dataTransfer?.getData('text/html') ?? '';
            if (files.length === 0 && !hasImageMarkup(html)) return false;

            event.preventDefault();
            const pos = getDropPosition(view, event) ?? view.state.selection.from;
            if (files.length > 0) {
              startFileUploads(view, files, pos);
            } else {
              insertHtmlWithImages(view, html, pos);
            }
            return true;
          }
        }
      })
    ];
  }
});

export default ArticleImageUpload;
